import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dumbbell, CheckCircle2, Sunrise, ArrowRight, Sparkles } from 'lucide-react';
import MorningYoga from './MorningYoga';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TodayWorkoutCard — shown on dashboard top.
 *   Props:
 *     onOpenBody: () => void   // open the "Kropp" tab
 *     onPointsChange: () => void
 */
export default function TodayWorkoutCard({ onOpenBody, onPointsChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showYoga, setShowYoga] = useState(false);
  const [completing, setCompleting] = useState(false);

  const fetchToday = async () => {
    try {
      const { data: d } = await axios.get(`${API}/body/today`, { withCredentials: true });
      setData(d);
    } catch (e) {
      // Silent — widget is optional
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchToday(); }, []);

  const markDone = async () => {
    if (!data || data.completed) return;
    setCompleting(true);
    try {
      const { data: resp } = await axios.post(
        `${API}/body/workout/${data.week}/${data.day_index}/complete`,
        {}, { withCredentials: true }
      );
      if (resp.bonus > 0) {
        toast.success(`+${resp.points_awarded}p! ${resp.bonus_reason || ''}`.trim());
      } else {
        toast.success(`✅ Klart! +${resp.points_awarded}p · Streak: ${resp.streak}`);
      }
      fetchToday();
      if (onPointsChange) onPointsChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setCompleting(false);
    }
  };

  if (loading || !data) return null;

  const firstThree = (data.workout?.exercises || []).slice(0, 3);
  const extraCount = Math.max(0, (data.workout?.exercises?.length || 0) - 3);
  const phase = data.phase || {};

  return (
    <>
      <div
        className="neo-card p-4 mb-4"
        style={{ background: `linear-gradient(135deg, ${phase.color || '#FFC8DD'}22, #FFE8F055)` }}
        data-testid="today-workout-card"
      >
        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div
              className="w-11 h-11 rounded-xl border-[3px] border-black flex items-center justify-center shrink-0 shadow-[3px_3px_0_#000]"
              style={{ background: phase.color || '#FFC8DD' }}
            >
              <span className="text-2xl">{data.workout?.emoji || '💪'}</span>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase opacity-60 tracking-wider">
                {data.day_name} · Vecka {data.week} · {phase.emoji} Fas {phase.id}
              </div>
              <h3 className="font-black text-base leading-tight truncate">
                {data.workout?.focus || 'Dagens pass'}
              </h3>
            </div>
          </div>

          {/* Yoga button */}
          <button
            onClick={() => setShowYoga(true)}
            className={`neo-btn px-3 py-1.5 flex items-center gap-1.5 text-xs font-bold whitespace-nowrap ${
              data.yoga_done_today ? 'bg-[#B9FBC0]' : 'bg-white'
            }`}
            data-testid="morning-yoga-btn"
            title="Starta dagen med 10 min yoga + meditation"
          >
            {data.yoga_done_today ? (
              <><CheckCircle2 className="w-3.5 h-3.5" /> Yoga klar</>
            ) : (
              <><Sunrise className="w-3.5 h-3.5" /> Morgonyoga 10m</>
            )}
          </button>
        </div>

        {/* Exercise peek */}
        {firstThree.length > 0 && (
          <ul className="space-y-1 mt-2" data-testid="today-workout-exercises">
            {firstThree.map((ex, i) => (
              <li key={i} className="flex items-baseline gap-2 text-xs">
                <span className="font-bold opacity-50 shrink-0">{i + 1}.</span>
                <span className="font-bold flex-1 truncate">{ex.name}</span>
                <span className="text-[10px] bg-white border border-black px-1.5 py-0.5 rounded-full shrink-0">
                  {ex.sets}
                </span>
              </li>
            ))}
            {extraCount > 0 && (
              <li className="text-[11px] opacity-60 italic pl-5">+ {extraCount} till…</li>
            )}
          </ul>
        )}

        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={markDone}
            disabled={data.completed || completing}
            className={`neo-btn flex-1 py-2 font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-60 ${
              data.completed ? 'bg-[#B9FBC0]' : 'bg-[#FFC8DD]'
            }`}
            data-testid="today-workout-complete-btn"
          >
            {data.completed ? (
              <><CheckCircle2 className="w-4 h-4" /> Klart ✨</>
            ) : completing ? (
              'Sparar...'
            ) : (
              <><Dumbbell className="w-4 h-4" /> Markera pass klart</>
            )}
          </button>
          <button
            onClick={onOpenBody}
            className="neo-btn px-3 py-2 bg-white text-sm flex items-center gap-1"
            data-testid="today-workout-open-btn"
            title="Öppna Kropp-fliken"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {!data.started && (
          <p className="text-[11px] opacity-60 mt-2 italic">
            💡 Klicka "Markera pass klart" eller öppna Kropp-fliken för att börja din 16-veckors resa — dagens räknas från idag.
          </p>
        )}
      </div>

      {showYoga && (
        <MorningYoga
          onClose={() => setShowYoga(false)}
          onComplete={() => { fetchToday(); if (onPointsChange) onPointsChange(); }}
        />
      )}
    </>
  );
}
